import React, { useState, useEffect } from "react";
import { NavLink, Link, Outlet, useNavigate, useLocation } from "react-router-dom";
import Lenis from "lenis";
import {
  Home, PiggyBank, Wallet, Gift, CalendarClock, Contact, GitCompare,
  Users2, HeartHandshake, ListChecks, FileText, CreditCard, UserCog,
  Users, Menu, X, LogOut, Heart, Sun, Moon, UserCircle, Mail, Link2, ShieldCheck,
  DollarSign, ReceiptText, Palette, MessageSquareQuote,
  Layout as LayoutIcon, Store, Sparkles as SparklesIcon, Phone, FileEdit, KeyRound,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useData } from "@/context/DataContext";
import { useFeatureAccess } from "@/hooks/useFeatureAccess";
import { getAnimal } from "@/lib/animals";
import { Button } from "@/components/ui/button";
import WhatsAppFab from "@/components/WhatsAppFab";

const MENU = [
  { to: "/app", label: "Home", icon: Home, end: true },
  { to: "/app/tabungan", label: "Tabungan", icon: PiggyBank, feature: "tabungan" },
  { to: "/app/budget", label: "Budget", icon: Wallet, feature: "budget" },
  { to: "/app/seserahan", label: "List Seserahan", icon: Gift, feature: "seserahan" },
  { to: "/app/rundown", label: "Rundown Acara", icon: CalendarClock, feature: "rundown" },
  { to: "/app/vendor", label: "Kontak Vendor", icon: Contact, feature: "vendor" },
  { to: "/app/perbandingan", label: "Perbandingan Vendor", icon: GitCompare, feature: "perbandingan" },
  { to: "/app/tamu", label: "List Tamu", icon: Users2, feature: "tamu" },
  { to: "/app/life", label: "Life After Married", icon: HeartHandshake, feature: "life" },
  { to: "/app/todo", label: "To Do List", icon: ListChecks, feature: "todo" },
  { to: "/app/dokumen", label: "List Dokumen", icon: FileText, feature: "dokumen" },
  { to: "/app/pembayaran", label: "Pembayaran", icon: CreditCard, feature: "pembayaran" },
  { to: "/app/undangan", label: "Kustomisasi Undangan", icon: Mail, feature: "undangan" },
  { to: "/app/moodboard", label: "Moodboard", icon: LayoutIcon, feature: "moodboard" },
  { to: "/app/skenario-budget", label: "Skenario Budget", icon: SparklesIcon, feature: "skenario_budget" },
  { to: "/app/vendor-pilihan", label: "Vendor Pilihan", icon: Store, feature: "vendor_pilihan" },
  { to: "/app/profile", label: "Profile", icon: UserCircle },
  { to: "/app/users", label: "User", icon: UserCog, sysadminOnly: true },
  { to: "/app/reg-links", label: "Link Pendaftaran", icon: Link2, adminAccess: true },
  { to: "/app/persetujuan-paket", label: "Persetujuan Paket", icon: ShieldCheck, adminAccess: true },
  { to: "/app/harga-paket", label: "Harga Paket", icon: DollarSign, adminAccess: true },
  { to: "/app/manajemen-pengguna", label: "Manajemen Pengguna", icon: Users2, adminAccess: true },
  { to: "/app/transaksi", label: "Riwayat Transaksi", icon: ReceiptText, adminAccess: true },
  { to: "/app/template-manager", label: "Manajemen Template", icon: Palette, adminAccess: true },
  { to: "/app/testimonials", label: "Testimonial Landing", icon: MessageSquareQuote, adminAccess: true },
  { to: "/app/konten-halaman", label: "Manajemen Konten", icon: FileEdit, adminAccess: true },
  { to: "/app/akses-fitur", label: "Manajemen Akses Fitur", icon: KeyRound, adminAccess: true },
  { to: "/app/vendor-listing", label: "Katalog Vendor", icon: Store, adminAccess: true },
  { to: "/app/skenario-budget-admin", label: "Skenario Budget Admin", icon: SparklesIcon, adminAccess: true },
  { to: "/app/settings-app", label: "Pengaturan Aplikasi", icon: Phone, adminAccess: true },
  { to: "/app/pasangan", label: "Pasangan", icon: Users, sysadminOnly: true },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { mode, toggleMode } = useTheme();
  const { activePasangan } = useData();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isSysadmin = user?.role === "sysadmin";
  const canSelectPasangan = user?.role === "sysadmin" || user?.role === "Admin";
  const { canAccess } = useFeatureAccess();
  const animal = isSysadmin ? getAnimal("harimau") : getAnimal(user?.animal);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  const doLogout = () => { logout(); navigate("/login"); };
  const visibleMenu = MENU.filter((m) => {
    if (m.sysadminOnly) return isSysadmin;
    if (m.adminAccess) return canSelectPasangan;
    if (m.feature && !canAccess(m.feature)) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-72 transform border-r border-border bg-background transition-transform duration-300 ${open ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex h-16 items-center justify-between border-b border-border px-5">
          <Link to="/app" data-testid="sidebar-brand-link" onClick={() => setOpen(false)} className="flex items-center gap-2">
            <img src="/wedifylab_logo3.png" alt="Wedifylab" className="h-8 w-8 object-contain" />
            <span className="font-heading text-xl font-semibold tracking-tight">Wedifylab</span>
          </Link>
          <button data-testid="sidebar-close" onClick={() => setOpen(false)} className="rounded-md p-1 hover:bg-muted">
            <X className="h-5 w-5" />
          </button>
        </div>
        <nav
          data-lenis-prevent
          data-testid="sidebar-nav"
          className="kh-scroll flex flex-col gap-1 overflow-y-auto overscroll-contain p-3"
          style={{ height: "calc(100dvh - 4rem)", maxHeight: "calc(100dvh - 4rem)", WebkitOverflowScrolling: "touch" }}
        >
          {visibleMenu.map((m) => (
            <NavLink
              key={m.to} to={m.to} end={m.end}
              data-testid={`nav-${m.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm transition-colors ${isActive ? "bg-primary text-primary-foreground font-semibold" : "text-muted-foreground hover:bg-muted"}`}
            >
              <m.icon className="h-4.5 w-4.5" style={{ width: 18, height: 18 }} />
              {m.label}
            </NavLink>
          ))}
        </nav>
      </aside>
      {open && <div className="fixed inset-0 z-40 bg-black/40" onClick={() => setOpen(false)} />}

      {/* Header */}
      <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-border bg-background/80 px-4 backdrop-blur-xl md:px-8">
        <div className="flex items-center gap-3">
          <button data-testid="menu-toggle" onClick={() => setOpen(true)} className="rounded-md p-2 hover:bg-muted">
            <Menu className="h-5 w-5" />
          </button>
          <Link to="/app" data-testid="header-brand-link" className="flex items-center gap-3 rounded-md px-1 py-1 transition-opacity hover:opacity-80" aria-label="Ke Dashboard">
            <img src="/wedifylab_logo3.png" alt="Wedifylab" className="h-7 w-7 object-contain" />
            <span className="font-heading text-lg font-semibold tracking-tight">Wedifylab</span>
          </Link>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 rounded-full border border-border p-1" role="radiogroup" aria-label="Mode tampilan">
            <button data-testid="mode-light" role="radio" aria-checked={mode === "light"} onClick={() => toggleMode("light")}
              className={`rounded-full p-1.5 ${mode === "light" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
              <Sun className="h-4 w-4" />
            </button>
            <button data-testid="mode-dark" role="radio" aria-checked={mode === "dark"} onClick={() => toggleMode("dark")}
              className={`rounded-full p-1.5 ${mode === "dark" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
              <Moon className="h-4 w-4" />
            </button>
          </div>
          <div className="hidden items-center gap-2 rounded-full border border-border px-3 py-1.5 text-sm md:flex">
            {user?.avatar
              ? <img src={user.avatar} alt="avatar" className="h-6 w-6 rounded-full object-cover" />
              : <span className="text-lg">{animal.emoji}</span>}
            <span className="max-w-[100px] truncate">{user?.username}</span>
          </div>
          <Button data-testid="logout-btn" onClick={doLogout} variant="ghost" size="icon">
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="min-h-[calc(100vh-8rem)] px-4 py-8 md:px-8">
        {canSelectPasangan && !activePasangan && !["/app", "/app/users", "/app/reg-links", "/app/pasangan", "/app/profile", "/app/persetujuan-paket", "/app/harga-paket", "/app/manajemen-pengguna", "/app/transaksi", "/app/template-manager", "/app/testimonials", "/app/konten-halaman", "/app/akses-fitur", "/app/vendor-listing", "/app/skenario-budget-admin", "/app/settings-app", "/app/vendor-pilihan", "/app/skenario-budget"].includes(location.pathname) ? (
          <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            Pilih pasangan di menu Home untuk melihat data.
          </div>
        ) : (
          <Outlet />
        )}
      </main>

      {/* Footer */}
      <footer className="flex items-center justify-center gap-2 border-t border-border py-6 text-sm text-muted-foreground">
        build with love <Heart className="h-4 w-4 fill-red-500 text-red-500" />
      </footer>

      <WhatsAppFab position="left" context="fitur aplikasi" />
    </div>
  );
}
